import { useEffect, useState, useMemo } from 'react';
import {
  X, Edit, Trash2, Eye, EyeOff, Calendar, Globe, Tag, Clock,
  TrendingUp, Monitor, Smartphone, Tablet, BarChart2, BookOpen,
  FileText, ExternalLink, Send,
} from 'lucide-react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, LineElement, BarElement,
  PointElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Post, fetchPostStats, PostStats, CustomRange, updatePost, deletePost } from '../lib/api';
import { Link, useNavigate } from 'react-router-dom';

ChartJS.register(CategoryScale, LinearScale, LineElement, BarElement, PointElement, Title, Tooltip, Legend, Filler);

const LANG_LABELS: Record<string, string> = {
  en: '🇬🇧 EN', tr: '🇹🇷 TR', de: '🇩🇪 DE', fr: '🇫🇷 FR',
  id: '🇮🇩 ID', pl: '🇵🇱 PL', pt: '🇧🇷 PT', ar: '🇸🇦 AR',
  hi: '🇮🇳 HI', ur: '🇵🇰 UR',
};

const STATUS_STYLES: Record<string, string> = {
  published: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  draft:     'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  disabled:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const STATUS_LABEL: Record<string, string> = {
  published: 'Published',
  draft: 'Draft',
  scheduled: 'Scheduled',
  disabled: 'Unpublished',
};

const PALETTE = ['#8B5CF6','#3B82F6','#10B981','#F59E0B','#EF4444','#06B6D4','#84CC16'];

function formatBucketLabel(ts: number, range: string): string {
  const d = new Date(ts * 1000);
  if (range === '24h') return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (['7d', '15d'].includes(range)) return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  if (['1m', '3m'].includes(range)) return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return d.toLocaleDateString([], { month: 'short', year: '2-digit' });
}

interface Props {
  post: Post;
  onClose: () => void;
  onRefresh: () => void;
  onScheduleRequest: (post: Post) => void;
}

export default function PostViewPanel({ post, onClose, onRefresh, onScheduleRequest }: Props) {
  const navigate = useNavigate();
  const [range, setRange] = useState('7d');
  const [stats, setStats] = useState<PostStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localPost, setLocalPost] = useState<Post>(post);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  // Keep local post in sync when parent prop changes
  useEffect(() => { setLocalPost(post); }, [post]);

  useEffect(() => {
    if (!localPost.slug) return;
    setStatsLoading(true);
    fetchPostStats(localPost.slug, range, 12)
      .then(s => { setStats(s); setStatsLoading(false); })
      .catch(() => setStatsLoading(false));
  }, [localPost.slug, range]);

  const changeStatus = async (newStatus: Post['status']) => {
    setBusy(true);
    try {
      const res = await updatePost(localPost.id, { status: newStatus });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(`Action failed (${res.status}) — ${(data as any).error || 'please try again'}`);
      }
      setLocalPost(p => ({ ...p, status: newStatus }));
      onRefresh();
      const label = newStatus === 'published' ? 'Published ✓' : newStatus === 'draft' ? 'Moved to Draft ✓' : `Status: ${newStatus} ✓`;
      showToast(label, true);
    } catch (e) {
      showToast((e as Error).message || 'Action failed — please try again', false);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      const res = await deletePost(localPost.id);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(`Delete failed (${res.status}) — ${(data as any).error || 'please try again'}`);
      }
      onClose();
      onRefresh();
    } catch (e) {
      showToast((e as Error).message || 'Delete failed — please try again', false);
      setBusy(false);
    }
  };

  const ptype = (localPost as any).post_type || 'blog';
  // tags may arrive as a JSON string from some API versions — normalize to array
  const tagsArray: string[] = Array.isArray(localPost.tags)
    ? localPost.tags
    : typeof localPost.tags === 'string' && (localPost.tags as string).trim()
      ? (() => { try { const p = JSON.parse(localPost.tags as unknown as string); return Array.isArray(p) ? p : []; } catch { return (localPost.tags as unknown as string).split(',').map((t: string) => t.trim()).filter(Boolean); } })()
      : [];
  const totalViews = stats?.count ?? 0;
  const peakViews = useMemo(() => Math.max(0, ...(stats?.series?.map(s => s.count) ?? [0])), [stats]);

  const lineData = {
    labels: (stats?.series ?? []).map(p => formatBucketLabel(p.bucketStart, range)),
    datasets: [{
      label: 'Views',
      data: (stats?.series ?? []).map(p => p.count),
      fill: true,
      backgroundColor: 'rgba(139,92,246,0.10)',
      borderColor: 'rgba(139,92,246,1)',
      borderWidth: 2,
      pointRadius: 2,
      tension: 0.4,
    }],
  };

  const makeBarData = (items: { label: string; count: number }[]) => ({
    labels: items.map(i => i.label),
    datasets: [{ data: items.map(i => i.count), backgroundColor: PALETTE, borderRadius: 4 }],
  });

  const lineOpts = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 } } },
    },
  } as const;

  const barOpts = {
    responsive: true,
    indexAxis: 'y' as const,
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 } } },
      y: { grid: { display: false }, ticks: { font: { size: 10 } } },
    },
  } as const;

  const RANGE_OPTIONS = [
    { key: '24h', label: '24h' }, { key: '7d', label: '7d' },
    { key: '1m', label: '1mo' }, { key: '3m', label: '3mo' },
    { key: '1y', label: '1yr' }, { key: 'all', label: 'All' },
  ];

  // Context-aware actions based on status
  const actions = () => {
    const s = localPost.status;
    return (
      <div className="flex flex-wrap gap-2">
        {/* Edit — always available */}
        <Link
          to={`/blog/edit/${localPost.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition"
        >
          <Edit size={13} /> Edit
        </Link>

        {/* Status-specific transitions */}
        {s === 'published' && (
          <button
            onClick={() => changeStatus('draft')}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium hover:bg-amber-600 disabled:opacity-50 transition"
          >
            <EyeOff size={13} /> Unpublish
          </button>
        )}
        {s === 'draft' && (
          <>
            <button
              onClick={() => changeStatus('published')}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50 transition"
            >
              <Eye size={13} /> Publish
            </button>
            <button
              onClick={() => onScheduleRequest(localPost)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-medium hover:bg-blue-600 disabled:opacity-50 transition"
            >
              <Calendar size={13} /> Schedule
            </button>
          </>
        )}
        {s === 'scheduled' && (
          <>
            <button
              onClick={() => changeStatus('published')}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50 transition"
            >
              <Send size={13} /> Publish Now
            </button>
            <button
              onClick={() => changeStatus('draft')}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-500 text-white text-xs font-medium hover:bg-gray-600 disabled:opacity-50 transition"
            >
              <EyeOff size={13} /> Back to Draft
            </button>
          </>
        )}
        {s === 'disabled' && (
          <button
            onClick={() => changeStatus('draft')}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-600 text-white text-xs font-medium hover:bg-gray-700 disabled:opacity-50 transition"
          >
            <FileText size={13} /> Move to Draft
          </button>
        )}

        {/* Delete — always available */}
        <button
          onClick={() => setShowDeleteConfirm(true)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-50 transition"
        >
          <Trash2 size={13} /> Delete
        </button>
      </div>
    );
  };

  return (
    <>
      {/* Delete confirm dialog */}
      {showDeleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[400]" onClick={() => setShowDeleteConfirm(false)} />
          <div className="fixed inset-0 z-[401] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full">
              <p className="text-gray-800 dark:text-gray-100 text-sm font-medium mb-5">
                Permanently delete this post? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                  Cancel
                </button>
                <button onClick={() => { setShowDeleteConfirm(false); handleDelete(); }}
                  className="flex-1 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-5 left-1/2 -translate-x-1/2 z-[500] px-5 py-3 rounded-xl shadow-2xl text-white text-sm font-medium flex items-center gap-2 ${toast.ok ? 'bg-emerald-600' : 'bg-red-600'}`}>
          {toast.ok ? '✓' : '✕'} {toast.msg}
        </div>
      )}

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl flex flex-col bg-white dark:bg-gray-900 shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900 z-10">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                ptype === 'guide'
                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
              }`}>
                {ptype === 'guide' ? <BookOpen size={10} /> : <FileText size={10} />}
                {ptype}
              </span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[localPost.status]}`}>
                {STATUS_LABEL[localPost.status] || localPost.status}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{LANG_LABELS[localPost.lang] || localPost.lang}</span>
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-snug">{localPost.title}</h2>
            <p className="text-xs text-gray-400 font-mono mt-0.5 truncate">{localPost.slug}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition flex-shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-5 space-y-6">
          {/* Actions */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Actions</h3>
            {actions()}
          </div>

          {/* Metadata grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-3">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Created</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {new Date(localPost.created_at * 1000).toLocaleDateString()}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-3">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Updated</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {new Date(localPost.updated_at * 1000).toLocaleDateString()}
              </p>
            </div>
            {localPost.publish_at && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 col-span-2">
                <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider mb-0.5">Scheduled for</p>
                <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  {new Date(localPost.publish_at * 1000).toLocaleString()}
                </p>
              </div>
            )}
            {localPost.word_count && (
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-3">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Word Count</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{localPost.word_count.toLocaleString()}</p>
              </div>
            )}
            {localPost.read_time_min && (
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-3">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Read Time</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{localPost.read_time_min} min</p>
              </div>
            )}
            {tagsArray.length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-3 col-span-2">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Tags</p>
                <div className="flex flex-wrap gap-1">
                  {tagsArray.map(tag => (
                    <span key={tag} className="px-2 py-0.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full text-xs text-gray-600 dark:text-gray-300">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {localPost.meta_description && (
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-3 col-span-2">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Meta Description</p>
                <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">{localPost.meta_description}</p>
              </div>
            )}
          </div>

          {/* Post body preview */}
          {localPost.body && (() => {
            const bodyHtml = localPost.body
              .replace(/ {2,}(#{1,3} )/g, '\n$1')
              .replace(/ {2,}(- )/g, '\n$1')
              .replace(/ {2,}(\d+\. )/g, '\n$1')
              .replace(/^### (.*$)/gim, '<h4 style="font-size:1rem;font-weight:600;margin:0.75rem 0 0.25rem">$1</h4>')
              .replace(/^## (.*$)/gim, '<h3 style="font-size:1.1rem;font-weight:700;margin:1rem 0 0.3rem">$1</h3>')
              .replace(/^# (.*$)/gim, '<h2 style="font-size:1.2rem;font-weight:700;margin:1rem 0 0.3rem">$1</h2>')
              .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
              .replace(/\*(.+?)\*/g, '<em>$1</em>')
              .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_: string, alt: string, src: string) => {
                const fullSrc = src.startsWith('http') ? src : `https://media.inboxme.space${src.startsWith('/') ? '' : '/'}${src}`;
                return `<img src="${fullSrc}" alt="${alt}" style="max-width:100%;border-radius:0.5rem;margin:0.5rem 0" />`;
              })
              .replace(/\[Video\]\(([^)]+)\)/g, (_: string, url: string) =>
                url.includes('catbox.moe')
                  ? `<video controls style="max-width:100%;border-radius:0.5rem;margin:0.5rem 0"><source src="${url}" /></video>`
                  : `<a href="${url}" target="_blank" rel="noopener">📹 Video</a>`
              )
              .replace(/\n/g, '<br />');
            return (
              <div>
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Post Preview</h3>
                {localPost.cover_image_url && (
                  <img
                    src={localPost.cover_image_url.startsWith('http') ? localPost.cover_image_url : `https://media.inboxme.space${localPost.cover_image_url.startsWith('/') ? '' : '/'}${localPost.cover_image_url}`}
                    alt="Cover"
                    className="w-full rounded-xl object-cover mb-3"
                    style={{ maxHeight: '200px' }}
                  />
                )}
                <div
                  className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed overflow-auto"
                  style={{ maxHeight: '400px' }}
                  dangerouslySetInnerHTML={{ __html: bodyHtml }}
                />
              </div>
            );
          })()}

          {/* Analytics section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Analytics</h3>
              <div className="flex gap-1">
                {RANGE_OPTIONS.map(r => (
                  <button
                    key={r.key}
                    onClick={() => setRange(r.key)}
                    className={`px-2 py-1 rounded-lg text-xs font-medium transition ${
                      range === r.key
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats summary */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-900/20 dark:to-purple-900/10 rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-purple-700 dark:text-purple-300">
                  {statsLoading ? '…' : totalViews.toLocaleString()}
                </p>
                <p className="text-[10px] text-purple-500 dark:text-purple-400 font-medium">Total Views</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-900/10 rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-blue-700 dark:text-blue-300">
                  {statsLoading ? '…' : peakViews.toLocaleString()}
                </p>
                <p className="text-[10px] text-blue-500 dark:text-blue-400 font-medium">Peak</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-900/10 rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300">
                  {statsLoading ? '…' : (stats?.breakdown?.countries?.length ?? 0)}
                </p>
                <p className="text-[10px] text-emerald-500 dark:text-emerald-400 font-medium">Countries</p>
              </div>
            </div>

            {/* Views chart */}
            {!statsLoading && stats && (
              <div className="bg-white dark:bg-gray-800/60 rounded-xl p-4 mb-4 border border-gray-100 dark:border-gray-700">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-3">Views over time</p>
                <Line data={lineData} options={lineOpts} height={80} />
              </div>
            )}

            {statsLoading && (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500" />
              </div>
            )}

            {/* Breakdown charts */}
            {!statsLoading && stats && (
              <div className="grid grid-cols-1 gap-4">
                {stats.breakdown.countries.length > 0 && (
                  <div className="bg-white dark:bg-gray-800/60 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-1">
                      <Globe size={11} /> Countries
                    </p>
                    <Bar data={makeBarData(stats.breakdown.countries.slice(0, 6))} options={barOpts} height={100} />
                  </div>
                )}
                {stats.breakdown.devices.length > 0 && (
                  <div className="bg-white dark:bg-gray-800/60 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-1">
                      <Monitor size={11} /> Devices
                    </p>
                    <div className="flex gap-3">
                      {stats.breakdown.devices.map((d, i) => (
                        <div key={d.label} className="flex-1 text-center">
                          <div className="text-lg font-bold" style={{ color: PALETTE[i % PALETTE.length] }}>
                            {d.count}
                          </div>
                          <div className="text-[10px] text-gray-500 dark:text-gray-400 capitalize">{d.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {stats.breakdown.referrers.length > 0 && (
                  <div className="bg-white dark:bg-gray-800/60 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-1">
                      <ExternalLink size={11} /> Referrers
                    </p>
                    <div className="space-y-1.5">
                      {stats.breakdown.referrers.slice(0, 5).map((r, i) => {
                        const max = stats.breakdown.referrers[0]?.count || 1;
                        return (
                          <div key={r.label} className="flex items-center gap-2 text-xs">
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
                            <span className="text-gray-600 dark:text-gray-300 truncate flex-1">{r.label}</span>
                            <div className="flex items-center gap-1.5 w-24">
                              <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                                <div
                                  className="h-1.5 rounded-full"
                                  style={{ width: `${(r.count / max) * 100}%`, backgroundColor: PALETTE[i % PALETTE.length] }}
                                />
                              </div>
                              <span className="text-gray-500 w-6 text-right">{r.count}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
